import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    InstagramBrandIcon,
    YouTubeBrandIcon,
    NCoreUIKitTheme,
    ToggleGroup,
    InputGroup,
    XBrandIcon,
    Separator,
    Accordion,
    Progress,
    Avatar,
    Button,
    Alert,
    Badge,
    Card,
    Chip,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Circle,
    Path,
    Rect,
    Svg,
    G
} from "react-native-svg";
import {
    Check,
    Mail
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const MailIcon = createKitIcon(Mail);

const ROASTS = [
    "light",
    "medium",
    "dark"
];

const GRINDS = [
    "whole",
    "filter",
    "espresso"
];

const DAYS = [
    9,
    10,
    11,
    12,
    13,
    14,
    15
];

const STARS = [
    1,
    2,
    3,
    4,
    5
];

const PRESS = [
    "press1",
    "press2",
    "press3",
    "press4"
];

const PLANS = [
    {
        isFeatured: false,
        id: "taste",
        features: [
            "f1",
            "f2",
            "f3",
            "f4"
        ],
        price: 290
    },
    {
        isFeatured: true,
        id: "daily",
        features: [
            "f1",
            "f2",
            "f3",
            "f4",
            "f5"
        ],
        price: 540
    },
    {
        isFeatured: false,
        id: "office",
        price: 1020,
        features: [
            "f1",
            "f2",
            "f3",
            "f4"
        ]
    }
];

const QUOTES = [
    "elif",
    "mert",
    "zeynep"
];

const FAQS = [
    "q1",
    "q2",
    "q3",
    "q4"
];

const FOOTER_COLUMNS = [
    "products",
    "company",
    "support",
    "follow"
];

const SOCIAL_ICONS = {
    l1: InstagramBrandIcon,
    l2: YouTubeBrandIcon,
    l3: XBrandIcon
};

const FOOTER_LINKS = [
    "l1",
    "l2",
    "l3",
    "l4"
];

const LEGAL = [
    "privacy",
    "terms",
    "cookies"
];

const NAV = [
    "coffees",
    "plans",
    "about",
    "faq"
];

const STAR_PATH = "M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26Z";

const BAG_BODY = "M38 36h84l9 126a7 7 0 0 1-7 7H36a7 7 0 0 1-7-7z";

const BAG_SHOULDER = "M38 36h84l1.4 20H36.6z";

const BAG_CAP = "M44 22c0-4 4-6 8-6h56c4 0 8 2 8 6v14H44z";

const BAG_SHINE = "M120 60l8 100";

const BAG_LEAF = "M74 106c2-8 8-12 14-12-1 7-5 12-14 12z";

const HERO_BAGS = [
    {
        scale: 1.9,
        bag: "2",
        x: 38,
        y: 115
    },
    {
        scale: 1.9,
        bag: "3",
        x: 558,
        y: 115
    },
    {
        scale: 2.4,
        bag: "1",
        x: 258,
        y: 19
    }
];

const HERO_CIRCLES = [
    {
        bag: "2",
        cx: 190,
        cy: 290,
        r: 140
    },
    {
        bag: "3",
        cx: 710,
        cy: 290,
        r: 140
    },
    {
        bag: "1",
        cx: 450,
        cy: 240,
        r: 190
    }
];

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    },
    footerBottom: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    footerBottomCompact: {
        alignItems: "flex-start",
        flexDirection: "column"
    },
    wrap: {
        justifyContent: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    start: {
        alignItems: "flex-start"
    },
    link: {
        alignItems: "flex-start"
    },
    heroArt: {
        aspectRatio: 900 / 440,
        width: "100%"
    },
    centered: {
        alignItems: "center",
        width: "100%"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    footerColumns: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignItems: "center"
    },
    text: {
        textAlign: "center"
    },
    grow: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    cardFull: {
        width: "100%"
    },
    newsletter: {
        width: "100%"
    }
});

type BagProps = {
    scale?: number;
    paper: string;
    fill: string;
    ink: string;
    x?: number;
    y?: number;
};

const Bag = ({
    scale = 1,
    paper,
    x = 0,
    y = 0,
    fill,
    ink
}: BagProps) => <G
    transform={`translate(${x} ${y}) scale(${scale})`}
>
    <Path
        d={BAG_BODY}
        fill={fill}
    />
    <Path
        d={BAG_SHOULDER}
        opacity={0.16}
        fill={ink}
    />
    <Path
        fill={fill}
        d={BAG_CAP}
    />
    <Rect
        opacity={0.22}
        height={6}
        width={72}
        fill={ink}
        rx={2}
        x={44}
        y={30}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={6}
        stroke={paper}
        opacity={0.14}
        d={BAG_SHINE}
        fill="none"
    />
    <Rect
        fill={paper}
        height={70}
        width={68}
        rx={6}
        x={46}
        y={78}
    />
    <Circle
        strokeWidth={4}
        stroke={fill}
        fill="none"
        cy={102}
        cx={80}
        r={12}
    />
    <Path
        d={BAG_LEAF}
        fill={fill}
    />
    <Rect
        opacity={0.8}
        height={4}
        width={44}
        fill={ink}
        y={124}
        rx={2}
        x={58}
    />
    <Rect
        opacity={0.35}
        height={3}
        width={28}
        fill={ink}
        rx={1.5}
        y={132}
        x={66}
    />
</G>;

const Landing01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        roast,
        setRoast
    ] = useState("medium");

    const [
        grind,
        setGrind
    ] = useState("filter");

    const [
        day,
        setDay
    ] = useState("12");

    const [
        frequency,
        setFrequency
    ] = useState("biweekly");

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isStacked = width < shapeConfigs.wideBreakpoint;

    const categorical = colors.chart.categorical;

    const categoricalArea = colors.chart.categoricalArea;

    const ink = colors.content.container.inverse;

    const paper = colors.content.container.default;

    const money = new Intl.NumberFormat(t("blocks.landing01.locale"), {
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const handlePress = () => undefined;

    const getColor = (key: string) => categorical[key as keyof typeof categorical];

    const getArea = (key: string) => categoricalArea[key as keyof typeof categoricalArea];

    const handleRoast = (value: string) => () => setRoast(value);

    const handleGrind = (value: string) => () => setGrind(value);

    const handleDay = (value: string | Array<string>) => setDay(typeof value === "string" && value !== "" ? value : day);

    const handleFrequency = (value: string | Array<string>) => setFrequency(typeof value === "string" && value !== "" ? value : frequency);

    const renderRoast = (key: string) => <Chip
        title={t(`blocks.landing01.roast.${key}`)}
        isSelected={roast === key}
        onPress={handleRoast(key)}
        variant="outline"
        type="neutral"
        key={key}
    />;

    const renderGrind = (key: string) => <Chip
        title={t(`blocks.landing01.grind.${key}`)}
        isSelected={grind === key}
        onPress={handleGrind(key)}
        variant="outline"
        type="neutral"
        key={key}
    />;

    const renderDay = (value: number) => <ToggleGroup.Item
        title={String(value)}
        value={String(value)}
        key={value}
    />;

    const renderNav = (key: string, index: number) => <Button
        variant={index === 0 ? "tonal" : "ghost"}
        title={t(`blocks.landing01.nav.${key}`)}
        onPress={handlePress}
        type="neutral"
        size="small"
        key={key}
    />;

    const renderPress = (key: string) => <Text
        variant="titleMediumSize"
        color="mid"
        key={key}
    >
        {t(`blocks.landing01.${key}`)}
    </Text>;

    const renderFeature = (feature: string, planId: string) => <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={feature}
    >
        <Icon
            icon={Check}
            color="mid"
        />
        <Text
            variant="bodyMediumSize"
            style={stylesheet.grow}
            color="high"
        >
            {t(`blocks.landing01.plan.${planId}.${feature}`)}
        </Text>
    </View>;

    const renderPlan = (plan: typeof PLANS[number]) => {
        const deliveries = frequency === "weekly" ? 4 : 2;

        return <Card
            style={[
                stylesheet.grow,
                {
                    minWidth: isStacked ? "100%" : shapeConfigs.sideListWidth
                },
                plan.isFeatured ? {
                    borderColor: colors.content.border.emphasized,
                    borderWidth: borders.selection
                } : undefined
            ]}
            variant="outline"
            key={plan.id}
        >
            <Card.Header>
                <Card.Title>
                    {t(`blocks.landing01.plan.${plan.id}.name`)}
                </Card.Title>
                {
                    plan.isFeatured ?
                        <Card.Action>
                            <Badge
                                title={t("blocks.landing01.plan.popular")}
                                variant="tonal"
                                type="primary"
                            />
                        </Card.Action>
                    :
                        null
                }
            </Card.Header>
            <Card.Content>
                <View
                    style={{
                        gap: spaces.spacingLg
                    }}
                >
                    <View
                        style={{
                            gap: spaces.spacingXs
                        }}
                    >
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="displaySmallSize"
                                color="high"
                            >
                                {money.format(plan.price)}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.landing01.plan.perDelivery")}
                            </Text>
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t(`blocks.landing01.plan.${plan.id}.lead`)}
                        </Text>
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.landing01.plan.monthly", [
                                deliveries,
                                money.format(plan.price * deliveries)
                            ])}
                        </Text>
                    </View>
                    <View
                        style={{
                            gap: spaces.spacingSm
                        }}
                    >
                        {plan.features.map((feature) => renderFeature(feature, plan.id))}
                    </View>
                </View>
            </Card.Content>
            <Card.Footer>
                <Button
                    title={t(`blocks.landing01.plan.${plan.id}.cta`)}
                    variant={plan.isFeatured ? "filled" : "outline"}
                    type={plan.isFeatured ? "primary" : "neutral"}
                    spreadBehaviour="stretch"
                    onPress={handlePress}
                />
            </Card.Footer>
        </Card>;
    };

    const renderQuote = (key: string) => <Card
        style={[
            stylesheet.grow,
            {
                minWidth: isStacked ? "100%" : shapeConfigs.sideListWidth
            }
        ]}
        variant="filled"
        key={key}
    >
        <Card.Content>
            <View
                style={{
                    gap: spaces.spacingLg
                }}
            >
                <Text
                    variant="readingLargeSize"
                    color="high"
                >
                    {t(`blocks.landing01.quote.${key}.text`)}
                </Text>
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Avatar
                        title={t(`blocks.landing01.quote.${key}.name`)}
                        size="small"
                    />
                    <View
                        style={stylesheet.grow}
                    >
                        <Text
                            variant="labelMediumSize"
                            numberOfLines={1}
                            color="high"
                        >
                            {t(`blocks.landing01.quote.${key}.name`)}
                        </Text>
                        <Text
                            variant="bodySmallSize"
                            numberOfLines={1}
                            color="mid"
                        >
                            {t(`blocks.landing01.quote.${key}.role`)}
                        </Text>
                    </View>
                </View>
            </View>
        </Card.Content>
    </Card>;

    const renderFaq = (key: string) => <Accordion.Item
        value={key}
        key={key}
    >
        <Accordion.Trigger
            title={t(`blocks.landing01.faq.${key}.q`)}
        />
        <Accordion.Content>
            {t(`blocks.landing01.faq.${key}.a`)}
        </Accordion.Content>
    </Accordion.Item>;

    const renderFooterColumn = (key: string) => <View
        style={[
            stylesheet.link,
            {
                minWidth: shapeConfigs.sideListWidth / 2,
                gap: spaces.spacingXs
            }
        ]}
        key={key}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {t(`blocks.landing01.footer.${key}`)}
        </Text>
        {
            FOOTER_LINKS.map((link) => <Button
                icon={key === "follow" ? SOCIAL_ICONS[link as keyof typeof SOCIAL_ICONS] : undefined}
                title={t(`blocks.landing01.footer.${key}.${link}`)}
                spreadBehaviour="free"
                onPress={handlePress}
                variant="link"
                type="neutral"
                size="small"
                key={link}
            />)
        }
    </View>;

    const renderLegal = (key: string) => <Button
        title={t(`blocks.landing01.footer.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderSection = (children: ReactNode, gap: number = spaces.spacingXxl) => <View
        style={[
            stylesheet.centered,
            {
                maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                paddingVertical: spaces.spacingXxl / 2,
                gap
            }
        ]}
    >
        {children}
    </View>;

    const renderHeading = (eyebrow: string, title: string) => <View
        style={[
            stylesheet.centered,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {eyebrow}
        </Text>
        <Text
            variant="headlineSmallSize"
            accessibilityRole="header"
            style={stylesheet.text}
            color="high"
        >
            {title}
        </Text>
    </View>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.header,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                    >
                        {t("blocks.landing01.brand")}
                    </Text>
                </View>
                {isStacked ? null : NAV.map(renderNav)}
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    isCompact ?
                        null
                    :
                        <Button
                            title={t("blocks.landing01.login")}
                            onPress={handlePress}
                            variant="outline"
                            type="neutral"
                            size="small"
                        />
                }
                <Button
                    title={t("blocks.landing01.start")}
                    onPress={handlePress}
                    variant="filled"
                    type="primary"
                    size="small"
                />
            </View>
        </View>
        <Separator/>
        {
            renderSection(<>
                <Badge
                    title={t("blocks.landing01.hero.badge")}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                />
                <Text
                    variant={isCompact ? "displaySmallSize" : isStacked ? "displayMediumSize" : "displayLargeSize"}
                    style={[
                        stylesheet.text,
                        {
                            maxWidth: shapeConfigs.contentMaxWidth
                        }
                    ]}
                    accessibilityRole="header"
                    color="high"
                >
                    {t("blocks.landing01.hero.title")}
                </Text>
                <Text
                    style={[
                        stylesheet.text,
                        {
                            maxWidth: shapeConfigs.readingMaxWidth
                        }
                    ]}
                    variant="bodyLargeSize"
                    color="mid"
                >
                    {t("blocks.landing01.hero.lead")}
                </Text>
                <View
                    style={[
                        stylesheet.wrap,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Button
                        title={t("blocks.landing01.start")}
                        onPress={handlePress}
                        variant="filled"
                        type="primary"
                        size="large"
                    />
                    <Button
                        title={t("blocks.landing01.browse")}
                        onPress={handlePress}
                        variant="outline"
                        type="neutral"
                        size="large"
                    />
                </View>
                <View
                    accessibilityLabel={t("blocks.landing01.hero.ratingLabel")}
                    style={[
                        stylesheet.wrap,
                        stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                    accessibilityRole="image"
                >
                    <Svg
                        width={iconSizes.medium * 5}
                        height={iconSizes.medium}
                        viewBox="0 0 120 24"
                    >
                        {
                            STARS.map((value, index) => <Path
                                transform={`translate(${index * 24} 0)`}
                                fill={categorical["4"]}
                                d={STAR_PATH}
                                key={value}
                            />)
                        }
                    </Svg>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.landing01.hero.rating")}
                    </Text>
                </View>
            </>, spaces.spacingLg)
        }
        {
            renderSection(<View
                style={[
                    stylesheet.centered,
                    {
                        maxWidth: shapeConfigs.wideBreakpoint
                    }
                ]}
            >
                <Svg
                    accessibilityLabel={t("blocks.landing01.hero.art")}
                    style={stylesheet.heroArt}
                    viewBox="0 0 900 440"
                    role="img"
                >
                    {
                        HERO_CIRCLES.map((circle) => <Circle
                            fill={getArea(circle.bag)}
                            key={circle.bag}
                            cx={circle.cx}
                            cy={circle.cy}
                            r={circle.r}
                        />)
                    }
                    {
                        HERO_BAGS.map((bag) => <Bag
                            fill={getColor(bag.bag)}
                            scale={bag.scale}
                            key={bag.bag}
                            paper={paper}
                            ink={ink}
                            x={bag.x}
                            y={bag.y}
                        />)
                    }
                </Svg>
            </View>)
        }
        {
            renderSection(<>
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.landing01.pressTitle")}
                </Text>
                <View
                    style={[
                        stylesheet.wrap,
                        {
                            columnGap: spaces.spacingXxl,
                            rowGap: spaces.spacingMd
                        }
                    ]}
                >
                    {PRESS.map(renderPress)}
                </View>
            </>, spaces.spacingMd)
        }
        {
            renderSection(<>
                {renderHeading(t("blocks.landing01.how.eyebrow"), t("blocks.landing01.how.title"))}
                <View
                    style={[
                        stylesheet.cardFull,
                        {
                            flexDirection: isStacked ? "column" : "row",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.grow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Card
                            style={stylesheet.grow}
                            variant="filled"
                        >
                            <Card.Content>
                                <View
                                    style={{
                                        gap: spaces.spacingMd
                                    }}
                                >
                                    <Text
                                        variant="labelMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.landing01.how.roast")}
                                    </Text>
                                    <View
                                        style={[
                                            stylesheet.wrap,
                                            {
                                                justifyContent: "flex-start",
                                                gap: spaces.spacingXs
                                            }
                                        ]}
                                    >
                                        {ROASTS.map(renderRoast)}
                                    </View>
                                    <Text
                                        variant="labelMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.landing01.how.grind")}
                                    </Text>
                                    <View
                                        style={[
                                            stylesheet.wrap,
                                            {
                                                justifyContent: "flex-start",
                                                gap: spaces.spacingXs
                                            }
                                        ]}
                                    >
                                        {GRINDS.map(renderGrind)}
                                    </View>
                                </View>
                            </Card.Content>
                        </Card>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Avatar
                                size="small"
                                title="1"
                            />
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {t("blocks.landing01.how.step1")}
                            </Text>
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.landing01.how.step1Text")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.grow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Card
                            style={stylesheet.grow}
                            variant="filled"
                        >
                            <Card.Content>
                                <View
                                    style={{
                                        gap: spaces.spacingMd
                                    }}
                                >
                                    <Text
                                        variant="labelMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.landing01.how.day")}
                                    </Text>
                                    <ToggleGroup
                                        title={t("blocks.landing01.how.day")}
                                        attachBehaviour="attached"
                                        onValueChange={handleDay}
                                        isClearEnabled={false}
                                        variant="outline"
                                        size="small"
                                        value={day}
                                    >
                                        {DAYS.map(renderDay)}
                                    </ToggleGroup>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.landing01.how.dayHint", [
                                            day
                                        ])}
                                    </Text>
                                </View>
                            </Card.Content>
                        </Card>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Avatar
                                size="small"
                                title="2"
                            />
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {t("blocks.landing01.how.step2")}
                            </Text>
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.landing01.how.step2Text")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.grow,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Card
                            style={stylesheet.grow}
                            variant="filled"
                        >
                            <Card.Content>
                                <View
                                    style={{
                                        gap: spaces.spacingMd
                                    }}
                                >
                                    <Alert
                                        description={t("blocks.landing01.how.shippedText")}
                                        title={t("blocks.landing01.how.shipped")}
                                        type="success"
                                    />
                                    <Progress
                                        title={t("blocks.landing01.how.roastedOn")}
                                        value={66}
                                    >
                                        <Progress.Track/>
                                    </Progress>
                                </View>
                            </Card.Content>
                        </Card>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Avatar
                                size="small"
                                title="3"
                            />
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {t("blocks.landing01.how.step3")}
                            </Text>
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.landing01.how.step3Text")}
                        </Text>
                    </View>
                </View>
            </>)
        }
        {
            renderSection(<>
                {renderHeading(t("blocks.landing01.plans.eyebrow"), t("blocks.landing01.plans.title"))}
                <Text
                    variant="bodyLargeSize"
                    style={stylesheet.text}
                    color="mid"
                >
                    {t("blocks.landing01.plans.lead")}
                </Text>
                <ToggleGroup
                    title={t("blocks.landing01.plans.frequency")}
                    onValueChange={handleFrequency}
                    attachBehaviour="attached"
                    style={{
                        alignSelf: "center"
                    }}
                    isClearEnabled={false}
                    value={frequency}
                    variant="outline"
                >
                    <ToggleGroup.Item
                        title={t("blocks.landing01.plans.biweekly")}
                        value="biweekly"
                    />
                    <ToggleGroup.Item
                        title={t("blocks.landing01.plans.weekly")}
                        value="weekly"
                    />
                </ToggleGroup>
                <View
                    style={[
                        stylesheet.cardFull,
                        {
                            flexDirection: isStacked ? "column" : "row",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    {PLANS.map(renderPlan)}
                </View>
            </>, spaces.spacingLg)
        }
        {
            renderSection(<>
                <Text
                    variant="headlineSmallSize"
                    accessibilityRole="header"
                    style={stylesheet.text}
                    color="high"
                >
                    {t("blocks.landing01.quotes.title")}
                </Text>
                <View
                    style={[
                        stylesheet.cardFull,
                        {
                            flexDirection: isStacked ? "column" : "row",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    {QUOTES.map(renderQuote)}
                </View>
            </>, spaces.spacingLg)
        }
        <View
            style={[
                stylesheet.centered,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.contentMaxWidth,
                    paddingVertical: spaces.spacingXxl / 2,
                    gap: spaces.spacingLg
                }
            ]}
        >
            {renderHeading(t("blocks.landing01.faq.eyebrow"), t("blocks.landing01.faq.title"))}
            <View
                style={stylesheet.cardFull}
            >
                <Accordion
                    defaultValue="q1"
                    variant="plain"
                >
                    {FAQS.map(renderFaq)}
                </Accordion>
            </View>
        </View>
        {
            renderSection(<Card
                style={stylesheet.cardFull}
                variant="filled"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.centered,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            style={[
                                stylesheet.text,
                                {
                                    maxWidth: shapeConfigs.readingMaxWidth
                                }
                            ]}
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.landing01.cta.title")}
                        </Text>
                        <Text
                            style={[
                                stylesheet.text,
                                {
                                    maxWidth: shapeConfigs.readingMaxWidth
                                }
                            ]}
                            variant="bodyLargeSize"
                            color="mid"
                        >
                            {t("blocks.landing01.cta.lead")}
                        </Text>
                        <Button
                            title={t("blocks.landing01.start")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            variant="filled"
                            type="primary"
                            size="large"
                        />
                    </View>
                </Card.Content>
            </Card>)
        }
        <Separator/>
        <View
            style={stylesheet.centered}
        >
            <View
                style={[
                    stylesheet.centered,
                    {
                        maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.footerColumns,
                        stylesheet.cardFull,
                        {
                            columnGap: spaces.spacingXl,
                            rowGap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.start,
                            {
                                minWidth: isCompact ? "100%" : shapeConfigs.cardWidth,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant="titleLargeSize"
                            color="high"
                        >
                            {t("blocks.landing01.brand")}
                        </Text>
                        <Text
                            style={{
                                maxWidth: shapeConfigs.sideListWidth
                            }}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.landing01.footer.lead")}
                        </Text>
                        <InputGroup
                            title={t("blocks.landing01.footer.email")}
                            style={stylesheet.newsletter}
                            spreadBehaviour="stretch"
                        >
                            <InputGroup.Addon
                                icon={MailIcon}
                            />
                            <InputGroup.Input
                                placeholder={t("blocks.landing01.footer.email")}
                                keyboardType="email-address"
                                inputMode="email"
                            />
                            <InputGroup.Addon
                                align="end"
                            >
                                <InputGroup.Button
                                    title={t("blocks.landing01.footer.join")}
                                    onPress={handlePress}
                                />
                            </InputGroup.Addon>
                        </InputGroup>
                    </View>
                    {FOOTER_COLUMNS.map(renderFooterColumn)}
                </View>
                <View
                    style={[
                        stylesheet.footerBottom,
                        stylesheet.cardFull,
                        isCompact && stylesheet.footerBottomCompact,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.landing01.footer.copyright")}
                    </Text>
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        {LEGAL.map(renderLegal)}
                    </View>
                </View>
            </View>
        </View>
    </ScrollView>;
};
export default Landing01;
